import { Link } from 'react-router-dom';
import type { SolPhotoSourceCard } from '../../data/sol/sol-contract';
import { formatInstant } from '../../lib/timezone';
import { captureDetailPath } from './sol-capture-route';
import { projectLabel } from './sol-wording';
import { useSolPhotoThumbUrl } from './use-sol-photo-thumb-url';

/**
 * A cited PHOTO, shown as itself, as a card that opens the Capture it belongs
 * to. M11 / I-071.
 *
 * **A PHOTO CARD — never a Capture card pretending to be one** (I-074). It
 * shows the thumbnail through the Capture screen's own signed-URL read path,
 * and says which Capture, before or after, its place in the order, and when it
 * was added (Crewlink's canonical timezone, as the Work Log — I-075).
 *
 * **THE MODEL SAW NONE OF THIS.** Sol was handed facts about the photo and
 * never a URL, a path or a byte; the picture is resolved here, on the client,
 * from an authorized card, after the answer is already written. Nothing Sol
 * says about a photo comes from looking at it, and the alt text names the photo
 * — which Capture, which class, which position — rather than describing what
 * is in it.
 *
 * The text under the card is the CAPTURE's caption, labelled as such. A photo
 * has no caption of its own in Crewlink, and this card does not pretend it has.
 */
export interface SolPhotoSourceCardLinkProps {
  readonly card: SolPhotoSourceCard;
  readonly timeZone: string | null;
}

export function photoCardTitle(card: SolPhotoSourceCard): string {
  const kind = card.photoClass === 'before' ? 'Before photo' : 'After photo';
  return `CAP-${String(card.captureNumber).padStart(4, '0')} · ${kind} ${card.position + 1}`;
}

export function SolPhotoSourceCardLink({ card, timeZone }: SolPhotoSourceCardLinkProps) {
  const project = projectLabel(card.project.name);
  const added = timeZone ? formatInstant(card.createdAt, timeZone) : '';
  const kind = card.photoClass === 'before' ? 'Before photo' : 'After photo';
  const title = photoCardTitle(card);
  const url = useSolPhotoThumbUrl(card);
  return (
    <Link
      className="sol-source sol-source--photo"
      to={captureDetailPath(card.project.id, card.captureId)}
      aria-label={`Open the Capture holding this ${kind.toLowerCase()}, ${title}, in ${project}`}
    >
      <span className="sol-source-photo-thumb">
        {url ? (
          <img
            className="sol-source-photo-image"
            src={url}
            alt={title}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className="sol-source-photo-placeholder">{kind}</span>
        )}
      </span>
      <span className="sol-source-photo-body">
        <span className="sol-source-head">
          <span className="sol-source-title">{title}</span>
          <span className="sol-source-status">Photo</span>
        </span>
        <span className="sol-source-meta">{added ? `${project} · Added ${added}` : project}</span>
        {card.captureCaption ? (
          <span className="sol-source-caption">
            <span className="sol-source-caption-label">Capture</span>
            {card.captureCaption}
          </span>
        ) : null}
        <span className="sol-source-open" aria-hidden="true">
          Open Capture →
        </span>
      </span>
    </Link>
  );
}
